import { useContext, useEffect, useState } from 'react'
import { MARKER_SUBREGEX } from './constants'
import { useInitMarkerProductData } from './marker-product-data'
import { UserDataContext } from '../services/user/user-data'

const getMarkerRegex = (replacementPattern: string) =>
  new RegExp(`\\$${replacementPattern}_${MARKER_SUBREGEX}`, 'g')

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getValueByPath = (obj: any, path: string): any => {
  if (!path) {
    return undefined
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const traverse = (root: any, segments: string[]) =>
    segments.reduce((current, key) => {
      if (current == null) {
        return undefined
      }
      if (Array.isArray(current)) {
        const [first] = current
        if (first == null) {
          return undefined
        }
        return first?.[key]
      }
      return current?.[key]
    }, root)

  if (path.includes('.')) {
    return traverse(obj, path.split('.'))
  }

  const direct = obj?.[path]
  if (direct !== undefined) {
    return direct
  }

  if (path.includes('_')) {
    return traverse(obj, path.split('_'))
  }

  return undefined
}

interface AdaptationPatternModel {
  markerData: { [textKey: string]: string }
  data: { [textKey: string]: string }
  pattern: string
  isEditMode: boolean
}

const getAdaptedPatternValue = ({
  markerData,
  data,
  pattern,
  isEditMode = false
}: AdaptationPatternModel) => {
  if (!markerData) {
    return undefined
  }

  const updatedData: { [key: string]: string } = {}
  const regex = getMarkerRegex(pattern)

  Object.entries(markerData).forEach(([key, value]) => {
    if (!value) {
      updatedData[key] = value
      return
    }

    const replaced = value.replace(regex, (_match, path) => {
      const replacement = getValueByPath(data, path)
      return replacement ?? (isEditMode ? `$${pattern}_${path}$` : '')
    })

    updatedData[key] = replaced
  })

  return updatedData
}

export {
  getValueByPath as _getValueByPath,
  getAdaptedPatternValue as _getAdaptedPatternValue
}

const MARKER_PATTERN = /\$\w+_[^$]*\$/g

const getMappedMarkerData = (
  markerData: { [key: string]: string },
  isEditMode: boolean = false
) =>
  isEditMode
    ? markerData
    : Object.entries(markerData).reduce(
        (accumulator, [key, value]) => ({
          ...accumulator,
          [key]: value ? value.replace(MARKER_PATTERN, '') : value
        }),
        {} as { [key: string]: string }
      )

export const useDataMarkerReplace = (
  markerData: { [key: string]: string },
  pattern: string,
  isEditMode: boolean = false
): { [key: string]: string } => {
  const [values, setValues] = useState(
    getMappedMarkerData(markerData, isEditMode)
  )

  const userData = useContext(UserDataContext)
  const productData = useInitMarkerProductData()

  const match = pattern?.match(/\$(\w+?)_/)

  const dataMarkerPattern = match ? match[1] : null

  useEffect(() => {
    const adaptedValue = getAdaptedPatternValue({
      markerData,
      data: {
        ...productData,
        ...(userData?.user?.profile ?? {})
      },
      pattern: dataMarkerPattern || '',
      isEditMode
    })
    // stop useEffect from being called in a loop if state didn't change
    if (
      JSON.stringify(values) !== JSON.stringify(adaptedValue) &&
      adaptedValue
    ) {
      setValues(adaptedValue)
    }
  }, [userData, markerData])

  return { ...values }
}
